import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { attachCurrentTime, dateDuration, getBookingBusinessDate, todayDate } from "../../utils/date";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { initAllStores } from "../../utils/cache";
import { sendRequest } from "../../utils/api";
import { v4 as uuidv4 } from "uuid";
import FullPageSpinner from "../../components/FullPageSpinner";
import { checkRoomAvailability } from "../../utils/booking-helper";
import BookedRoom from "../../models/BookedRoom";
import Hotel from "../../models/Hotel";
import { DEFAULT_CUSTOMER_EMAIL, LATE_CHECKOUT_AMOUNT, LATE_CHECKOUT_DURATION } from "../../constants";
import { getUserHotel } from "../../utils/user-helper";
import { terminal } from "../../utils/user-helper";
import { FaTimes } from "react-icons/fa";

interface ExtendStayModalProps {
  guest: {
    guestName: string;
    roomNo: string;
    roomId: string;
    bookingId: string;
    roomRate: number;
    guestEmail?: string;
  };
  isLateCheckout?: boolean;
  isPaidByGuest?: boolean;
  onClose: () => void;
  onExtend?: (data: BookedRoom) => void;
}

const today = new Date().toISOString().split("T")[0];

export default function ExtendStayModal({ guest, isLateCheckout, isPaidByGuest = false, onClose, onExtend, }: ExtendStayModalProps) {
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const [terminalId] = useState(() => terminal());

  const [checkInDate, setCheckInDate] = useState(today);
  const [checkOutDate, setCheckOutDate] = useState(isLateCheckout ? today : "");
  const [nights, setNights] = useState(0);
  const [discount, setDiscount] = useState("");
  const [total, setTotal] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [loading, setLoading] = useState(false);

  const [hotel, setHotel] = useState<Hotel | null>({} as Hotel);
  const [user, setUser] = useState({} as any);


  const businessDate = getBookingBusinessDate();

  const isPaid: boolean = paymentStatus.toLocaleLowerCase() === "paid";

  const data: BookedRoom = {
    id: uuidv4(),
    guestName: guest.guestName,
    createdAt: businessDate,
    updatedAt: todayDate(),
    bookingType: isLateCheckout ? "lateCheckOut" : "fullTime",
    checkedInById: user.id,
    clearedById: isPaid ? user.id : null,
    bookingId: guest.bookingId,
    roomId: guest.roomId,
    checkInDate: attachCurrentTime(checkInDate),
    checkOutDate,
    isUse: true,
    duration: nights,
    terminal: terminalId,
    amount: total + parseInt(discount || "0"),
    discount: parseInt(discount || "0"),
    isPaid: isPaidByGuest ? true : isPaid,
    paymentMethod: paymentMethod as PaymentType,
  };


  useEffect(() => {
    async function starterData() {
      const data = await getUserHotel();
      if (data) {
        const { hotel, user } = data;
        setHotel(hotel);
        setUser(user);
      } else if (!data && isPaidByGuest) {
        const hotelStr = localStorage.getItem("hotel");
        const userStr = localStorage.getItem("user");

        const hotel = JSON.parse(hotelStr ?? "{}");
        const user = JSON.parse(userStr ?? "{}");

        setHotel(hotel);
        setUser(user);

      } else {
        return openModal(
          "Session Expired ⚠️",
          "We couldn’t find your user information. Please log in again to continue.",
          () => {
            closeModal();
            window.location.href = "/login";
          },
          false,
          "Login Again"
        );
      }
      if (isPaidByGuest) setPaymentMethod("transfer");
    }
    starterData();
  }, []);

  useEffect(() => {

    if (checkInDate && checkOutDate && hotel) {
      const roomRate = isLateCheckout
        ? (hotel?.preference?.lateCheckoutAmount ?? LATE_CHECKOUT_AMOUNT)
        : guest.roomRate;
      const diff = dateDuration(checkInDate, checkOutDate);
      setNights(diff);
      setTotal(
        isLateCheckout
          ? roomRate : diff * roomRate - parseInt(discount || "0")
      );
    }
  }, [checkInDate, checkOutDate, discount, guest.roomRate, hotel, isLateCheckout]);


  if (!hotel || !user) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <FullPageSpinner show={loading} />
      </div>
    );
  }

  const roomRate = isLateCheckout
    ? (hotel?.preference?.lateCheckoutAmount ?? LATE_CHECKOUT_AMOUNT)
    : guest.roomRate;

  const lateCheckOutDuration =
    hotel?.preference?.lateCheckoutDuration ?? LATE_CHECKOUT_DURATION;

  const validateForm = (): boolean => {
    if (!checkInDate) {
      showToast("Please select a check-in date.", { type: "warning" });
      return false;
    }
    if (!checkOutDate) {
      showToast("Please select a check-out date.", { type: "warning" });
      return false;
    }
    if (nights <= 0) {
      showToast("Duration must be at least 1 night.", { type: "warning" });
      return false;
    }
    return true;
  };

  const initiatePayment = async () => {

    try {
      setLoading(true);

      // Check Room Available
      const checkUrl = `/rooms/${guest.roomId}/check-availability`;

      await sendRequest("POST", checkUrl, {
        bookingId: guest.bookingId,
        checkInDate: data.checkInDate,
        checkOutDate: data.checkOutDate
      });


      const callback_url = (
        `${window.location.origin}/payment/verify?billId=${guest.bookingId}&type=extendBooking`
      );

      const url = "/payment/initiate";
      const response = await sendRequest("POST", url, {
        amount: total, callback_url,
        email: guest?.guestEmail ?? DEFAULT_CUSTOMER_EMAIL,
      });

      localStorage.setItem(
        "data", JSON.stringify(data)
      );

      window.location.href = response.data.data.checkout_url;
    } catch (err: any) {
      console.log(err)
      if (err?.response?.status === 409) {
        return openModal(
          "Conflict⚠️",
          err.response.data.error,
          () => { },
          false,
          "Close"
        );
      }
      showToast(
        "Stay extension failed. " +
        "Please check your network and try again.",
        { type: "error" }
      );
    } finally {
      setLoading(false);
    }
  }

  const extendStay = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const cache = await initAllStores();
      const bookedRooms: BookedRoom[] = await cache.getAllBy("bookedRooms", {
        isUse: true, roomId: guest.roomId, bookingId: guest.bookingId,
      });

      if (!isLateCheckout) {
        const conflictMsg = await checkRoomAvailability(
          bookedRooms,
          guest.roomId,
          checkInDate,
          checkOutDate
        );

        if (conflictMsg) {
          return openModal("Room Conflict ⚠️", conflictMsg, () => closeModal(), false, "Close");
        }
      }

      const duration = isLateCheckout ? `${lateCheckOutDuration} Hrs` : `${nights} night`;
      openModal(
        isLateCheckout ? "Confirm Late Checkout" : "Confirm Extension",
        `You are extending ${guest.guestName}'s stay for ${duration} , `
        + `totaling ₦${total.toLocaleString()}. Proceed?`,
        async () => {
          closeModal();
          setLoading(true);
          try {
            const cache = await initAllStores();

            await sendRequest("POST", `/bookings/${guest.bookingId}/extend`, data);
            cache.saveOne("bookedRooms", {
              ...data, checkedInBy: { ...user },
              clearedBy: isPaid ? { ...user } : null,
            });

            // 👇 trigger parent callback with payload
            if (onExtend) onExtend({ ...data, roomNumber: guest.roomNo });
            showToast("Guest stay extended successfully!", { type: "success" });
            onClose();
          } catch (err: any) {
            console.log(err);
            showToast("Failed to extend stay. Please try again.", { type: "error" });
          } finally {
            setLoading(false);
          }
        },
        true,
        "Confirm"
      );
    } catch (err: any) {
      console.error(err);
      showToast("Failed to extend stay. Please try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <FullPageSpinner show={loading} />

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-[550px] max-h-[90vh] overflow-y-auto p-6"
      >
        {/* Close button */}

        <h2 className="text-xl font-semibold mb-2 text-gray-800">
          {isLateCheckout ? "Late Checkout" : "Extend Stay"} for {guest.guestName}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Room {guest?.roomNo} — ₦{roomRate?.toLocaleString()} {
            isLateCheckout ? `Late Checkout: ₦${roomRate} / ${lateCheckOutDuration} hrs`
              : "per night"
          }
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Check-in */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Check-in Date</label>
            <input
              type="date"
              value={checkInDate}
              disabled={isLateCheckout}
              onChange={(e) => {
                if (new Date(e.target.value) < new Date(today)) {
                  return showToast("Check-in date cannot be in the past.", { type: "warning" });
                } else if (new Date(e.target.value) > new Date(checkOutDate)) {
                  return showToast("Check-in date cannot exceed checkout date.", { type: "warning" });
                }
                setCheckInDate(e.target.value);
              }}
              className="w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          {/* Check-out */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Check-out Date</label>
            <input
              type="date"
              disabled={isLateCheckout}
              value={checkOutDate}
              onChange={(e) => {
                if (!checkInDate) {
                  return showToast("Please select a check-in date first.", { type: "warning" });
                }
                if (new Date(e.target.value) < new Date(checkInDate)) {
                  return showToast("Checkout date must be after check-in date.", {
                    type: "warning",
                  });
                }
                setCheckOutDate(e.target.value);
              }}
              className="w-full border border-gray-300 rounded-md p-2"
            />
          </div>

          {/* Duration */}
          {!isLateCheckout && <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Duration</label>
            <input
              type="text"
              value={`${nights || 0} night(s)`}
              readOnly
              className="w-full border border-gray-200 bg-gray-50 rounded-md p-2"
            />
          </div>}

          {/* Discount */}
          {!isLateCheckout && !isPaidByGuest && <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Discount (₦)</label>
            <input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
            />
          </div>}

          {/* Payment Method */}
          {!isPaidByGuest && <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2"
            >
              <option value="cash">Cash</option>
              <option value="pos">POS</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>}

          {/* Payment Status */}
          {!isPaidByGuest && <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Payment Status</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
              className="w-full border border-gray-300 rounded-md p-2"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </div>}
        </div>

        {/* Total */}
        <div className="flex justify-between items-center border-t pt-3 mt-5">
          <p className="text-gray-700 font-semibold">Total Amount:</p>
          <span className="text-lg font-bold text-gray-700">
            ₦{isNaN(total) ? "0" : total.toLocaleString()}
          </span>
        </div>

        {/* Buttons */}
        <div className="flex justify-end mt-6 space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300 text-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (isPaidByGuest) initiatePayment();
              else extendStay();
            }}
            disabled={!checkInDate || !checkOutDate}
            className={`px-4 py-2 rounded-md text-white ${!checkInDate || !checkOutDate
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
              }`}
          >
            Confirm
          </button>
        </div>
      </motion.div>
    </div>
  );
}